'use client'

import { useRef } from 'react'
import { useSearchParams } from 'next/navigation'
import { liteClient as algoliasearch } from 'algoliasearch/lite'
import { Configure, Hits, SearchBox } from 'react-instantsearch'
import { InstantSearchNext } from 'react-instantsearch-nextjs'

import FilterSortToolbar from '@/components/filters/FilterSortToolbar'
import FilterStats from '@/components/filters/FilterStats'
import FilterPagination from '@/components/filters/FilterPagination'
import SearchResultsFilters from '@/components/search/results/SearchResultsFilters'
import SearchResultsHit from '@/components/search/results/SearchResultsHit'

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID,
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY,
)

export default function SearchResults() {
  const searchParams = useSearchParams()
  const keyword = searchParams.get('q')
  const gridRef = useRef()

  return (
    <>
      <h1 className="sr-only">Search Results</h1>

      <InstantSearchNext
        searchClient={searchClient}
        indexName="wp_searchable_posts"
        routing={{
          router: {
            cleanUrlOnDispose: false,
            createURL({ qsModule, routeState, location }) {
              const instantSearchQuery = qsModule.stringify(routeState)
              const finalParams = new URLSearchParams(instantSearchQuery)
              const managedKeys = new Set(finalParams.keys())

              const existingParams = new URLSearchParams(location.search)
              for (const [key, value] of existingParams.entries()) {
                if (!managedKeys.has(key)) {
                  finalParams.append(key, value)
                }
              }

              const queryString = finalParams.toString()
              return `${location.pathname}${queryString ? `?${queryString}` : ''}`
            },
          },
          stateMapping: {
            stateToRoute(uiState) {
              const indexUiState = uiState['wp_searchable_posts'] || {}
              return {
                q: indexUiState.query,
              }
            },
            routeToState(routeState) {
              return {
                ['wp_searchable_posts']: {
                  query: routeState.q,
                },
              }
            },
          },
        }}>
        <Configure analytics={false} hitsPerPage={14} distinct={true} query={keyword || ''} />

        <section className="hero hero-padding bg-theme text-fg theme--base-0">
          <div className="bg-theme px-xxs py-l text-fg gradient-angle-[180deg] theme--gradient-light gradient--master ms-up:rounded-lg">
            <div className="container">
              <SearchBox
                placeholder="Search"
                classNames={{
                  root: 'w-full max-w-[824px] mx-auto',
                  submit: 'sr-only',
                  reset: 'sr-only',
                  loadingIndicator: 'hidden',
                }}
              />
            </div>
          </div>
        </section>

        <SearchResultsFilters attribute="post_type_label" sortBy={['name:asc']} operator="or" />

        <div ref={gridRef} className="container container--m mt-l s:mt-s">
          <FilterSortToolbar />
        </div>

        <div className="grid grid-cols-1 gap-y-xl" style={{ scrollMarginTop: '80px' }}>
          <Hits hitComponent={SearchResultsHit} className="container container--m mb-l" />
        </div>

        <div className="my-m border-t pt-m text-center">
          <div className="container flex items-center justify-between ms:flex-col ms:gap-y-s">
            <FilterStats />
            <FilterPagination gridRef={gridRef} />
          </div>
        </div>
      </InstantSearchNext>
    </>
  )
}
